import React, {useEffect} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {RootState} from '../../../../setup'
import {fetchOne} from '../redux/MaterialIssuanceAction'
import {Route, Breadcrumbs} from '../../../components/Breadcrumbs'
import {formatDate, formatDateTime} from '../../../helper'
import {PrintButton} from '../../../components/PrintButton'
import QRCode from 'qrcode.react'
import {Loader} from '../../../components/Loader'
import {NoItemFound} from '../../../components/NoItemFound'

type Props = {
  className: string
}

export const MaterialIssuanceDetail: React.FC<Props> = ({className}) => {
  const materialIssuance: any = useSelector<RootState>(
    ({materialIssuance}) => materialIssuance.materialIssuance,
    shallowEqual
  )

  const loading: any = useSelector<RootState>(
    ({materialIssuance}) => materialIssuance.loading,
    shallowEqual
  )

  const dispatch: any = useDispatch()
  let {id} = useParams<{id: any}>()
  useEffect(() => {
    dispatch(fetchOne(id))
  }, [dispatch, id])

  let routes: Route[] = [
    {name: 'Dashboard', url: '/dashboard'},
    {name: 'Material Issuances List', url: '/material-issuance'},
    {name: 'Material Issuance Detail', url: ''},
  ]

  return (
    <>
      <div className={`card card-custom print ${className}`}>
        <a href='/' className='print-view-show'>
          <img alt='Logo' src='https://nextbridge.com/wp-content/uploads/2022/02/Group-187.svg' />
        </a>
        <Breadcrumbs routes={routes} />
        <div className='card-header border-0 pt-5'>
          <h1 className='card-title'>
            <span className='card-label fw-bolder fs-3 mb-1'>Material Issuance Detail</span>
          </h1>
          <PrintButton />
        </div>
        <div className='card-body py-10'>
          {!loading ? (
            materialIssuance && materialIssuance?._id !== '' ? (
              <>
                <table className='table tbl-border tbl-md-responsivness tbl-responsivness-noLabel'>
                  <tbody>
                    <tr>
                      <td>
                        <b>Material Issuance No.</b>
                      </td>
                      <td>{materialIssuance?.number}</td>
                      <td>
                        <b>Created Date</b>
                      </td>
                      <td>{formatDateTime(materialIssuance?.createdAt)}</td>
                    </tr>
                    <tr>
                      <td>
                        <b>Material Requisition No.</b>
                      </td>
                      <td>{materialIssuance?.materialRequisition?.number}</td>
                      <td>
                        <b>Request by Employee Name</b>
                      </td>
                      <td>{materialIssuance?.providedToEmployee?.fullName}</td>
                    </tr>
                    <tr>
                      <td>
                        <b>Provided to Department Name</b>
                      </td>
                      <td>{materialIssuance?.providedToDepartment?.name}</td>
                      <td>
                        <b>Location</b>
                      </td>
                      <td>{materialIssuance?.location?.name}</td>
                    </tr>
                    <tr>
                      <td>
                        <b>Status</b>
                      </td>
                      <td>{materialIssuance?.status}</td>
                      <td colSpan={2}></td>
                    </tr>
                    {materialIssuance?.approvedAt && materialIssuance?.approvedBy && (
                      <tr>
                        <td>
                          <b>Approved At</b>
                        </td>
                        <td>{formatDateTime(materialIssuance?.approvedAt)}</td>
                        <td>
                          <b>Approved By</b>
                        </td>
                        <td>{materialIssuance?.approvedBy?.fullName}</td>
                      </tr>
                    )}
                    {materialIssuance?.rejectedAt && materialIssuance?.rejectedBy && (
                      <tr>
                        <td>
                          <b>Rejected At</b>
                        </td>
                        <td>{formatDateTime(materialIssuance?.rejectedAt)}</td>
                        <td>
                          <b>Rejected By</b>
                        </td>
                        <td>{materialIssuance?.rejectedBy?.firstName}</td>
                      </tr>
                    )}
                    {materialIssuance?.rejectionReason && (
                      <tr>
                        <td>
                          <b>Reason to Reject</b>
                        </td>
                        <td colSpan={3}>{materialIssuance?.rejectionReason}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <table className='table tbl-border tbl-md-responsivness table-striped mt-10'>
                  <thead>
                    <tr>
                      <th style={{width: 40}}>
                        <b>#</b>
                      </th>
                      <th>
                        <b>Product</b>
                      </th>
                      <th>
                        <b>UOM</b>
                      </th>
                      <th>
                        <b>Quantity</b>
                      </th>
                      <th>
                        <b>Asset No.</b>
                      </th>
                      <th>
                        <b>Expiry Date</b>
                      </th>
                      <th>
                        <b>QR Code</b>
                      </th>
                      <th>
                        <b>Remarks</b>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialIssuance.items.map((materialItem: any, index: any) => (
                      <tr key={index}>
                        <td data-label='#'>{index + 1}</td>
                        <td data-label='Name'>{materialItem.name}</td>
                        <td data-label='UOM'>{materialItem.UOM}</td>
                        <td data-label='Quantity'>{materialItem.quantity}</td>
                        <td data-label='Asset No.'>{materialItem.assetNumber}</td>
                        <td data-label='Expiry Date'>
                          {materialItem.expiryDate ? formatDate(materialItem.expiryDate) : '-'}
                        </td>
                        <td data-label='QR Code'>
                          {materialItem?.qrCode && (
                            <div
                              style={{
                                height: 'auto',
                                margin: '0 auto',
                                maxWidth: 64,
                                width: '100%',
                              }}
                            >
                              <QRCode
                                id={`${materialItem.qrCode}`}
                                size={144}
                                bgColor='#FFF'
                                fgColor='#000'
                                includeMargin
                                level={'H'}
                                style={{height: 'auto', maxWidth: '100%', width: '100%'}}
                                value={materialItem.qrCode.toString()}
                              />
                            </div>
                          )}
                        </td>
                        <td data-label='Remarks'>{materialItem.remarks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <NoItemFound />
            )
          ) : (
            <Loader />
          )}
        </div>
      </div>
    </>
  )
}
